import { parse } from 'querystring';

import {
  BundleItem,
  BundleItemOption,
  BundleProduct,
  ProductInterface,
} from '@storefront/data-commerce';
import urlQueryHandler from '@storefront/lib/query/queryParamsUtils';
import currency from 'currency.js';
import { ReadonlyURLSearchParams } from 'next/navigation';

import {
  BundleItemWithLowestPrice,
  BundleProductWithLowestPrice,
} from '../product-configuration/product-configuration.utils';

export type ActiveProductState = {
  active: Record<string, boolean>;
  setActive: (sku: string, bool: boolean) => void;
  getActive: () => Record<string, boolean>;
  getFirstActiveProduct: () => string;
  resetActive: () => void;
};

export type ProductData = {
  data: {
    products: {
      items: BundleProduct[];
    };
  };
};

type LowestOptionPrice = {
  product: ProductInterface;
  bundlePrice: number;
  cheapestOptionPrice: number;
  option: BundleItemOption;
};

export const hasOptionsWord = (wordInput: string) =>
  wordInput.includes('options');

export const optionsIsNotRegularParams = (option: string) => {
  const regularParams = ['skus', 'pdpFlow'];
  return !regularParams.includes(option);
};

function getProductPrice(product: ProductInterface): number {
  if (!product) {
    return 0;
  }
  return (
    product.hp_as_low_as_price ||
    product.price_range.maximum_price.regular_price.value
  );
}
export function calculateLowestPrice(items: BundleItem[]): number {
  return items.reduce((acc, { options, required }) => {
    if (!required) {
      return acc;
    }

    const firstOptionByPosition = options?.reduce((acc, options) =>
      options.position < acc.position ? options : acc
    );

    const price = getProductPrice(firstOptionByPosition.product);

    return acc.add(price);
  }, currency(0)).value;
}

const getCheapestOptionPrice = (options: BundleItemOption[]) => {
  const allOptionsPrices = options.map((option) =>
    getProductPrice(option.product)
  );
  return Math.min(...allOptionsPrices);
};

const addLowestPriceToOption = ({
  product,
  bundlePrice,
  cheapestOptionPrice,
  option,
}: LowestOptionPrice) => {
  const currentOptionPrice = getProductPrice(product);
  const bundlePriceLessCheapestOption =
    currency(bundlePrice).subtract(cheapestOptionPrice);
  const optionLowestPrice =
    bundlePriceLessCheapestOption.add(currentOptionPrice).value;
  return { lowestPrice: optionLowestPrice, product, ...option };
};

const getSortedOptionsWithLowestPrice = (
  options: BundleItemOption[],
  bundlePrice: number
) =>
  options
    .map(({ product, ...option }) =>
      addLowestPriceToOption({
        product,
        bundlePrice,
        cheapestOptionPrice: getCheapestOptionPrice(options),
        option,
      })
    )
    .sort((a, b) => a.lowestPrice - b.lowestPrice);

const getBundleItemWihLowestPrice = (
  items: BundleItem[],
  bundleLowestPrice: number
) =>
  items.map(({ options, ...others }) => ({
    options: getSortedOptionsWithLowestPrice(options, bundleLowestPrice),
    ...others,
  }));

export function addLowestPriceToEachItem(
  items: BundleItem[],
  bundleLowestPrice: number
): BundleItemWithLowestPrice[] {
  return getBundleItemWihLowestPrice(items, bundleLowestPrice);
}

export const isSelectedItemsOutOfStock = (
  items: BundleProductWithLowestPrice[],
  selectedSku: string
): boolean =>
  Boolean(
    items?.find(
      (item) =>
        item?.sku === selectedSku && item?.stock_status === 'OUT_OF_STOCK'
    )
  );

export const getQueryParams = (query: ReadonlyURLSearchParams): any => {
  if (!query) return {};
  return urlQueryHandler(parse(query.toString()))();
};
